import React from 'react';
import { FiActivity } from 'react-icons/fi';
import LessonTemplate from './components/LessonTemplate';
import QuantLessonPracticeSlot from './components/QuantLessonPracticeSlot';

const AlgebraLesson = () => {
  const sections = [
    {
      id: 'linear-equations',
      title: 'Linear Equations',
      content: {
        introduction:
          'Linear equations are equations where the highest power of the variable is 1. They form straight lines when graphed and are fundamental to algebra.',

        concepts: [
          {
            title: 'Standard Form',
            explanation:
              'A linear equation in one variable has the form §ax + b = c§, where §a ≠ 0§. The GRE method is to isolate the variable: undo addition/subtraction first, then undo multiplication/division.',
            example: {
              problem: 'Solve: §3x + 7 = 22§',
              solution: [
                'Subtract 7 from both sides: §3x = 22 - 7 = 15§',
                'Divide both sides by 3: §x = 15/3 = 5§',
                'Check: §3(5) + 7 = 15 + 7 = 22§ ✓',
              ],
            },
          },
          {
            title: 'Linear Equations in Two Variables',
            explanation:
              'Linear equations in two variables have the form §ax + by = c§ and represent lines in the coordinate plane.',
            example: {
              problem: 'Find the y-intercept of §2x + 3y = 12§',
              solution: [
                'Set §x = 0§: §2(0) + 3y = 12§',
                'Simplify: §3y = 12§',
                'Solve: §y = 4§',
                'The y-intercept is (0, 4)',
              ],
            },
          },
          {
            title: 'Slope-Intercept Form',
            explanation:
              'The slope-intercept form §y = mx + b§ makes it easy to identify slope (m) and y-intercept (b).',
            example: {
              problem: 'Convert §4x - 2y = 8§ to slope-intercept form',
              solution: [
                'Start with: §4x - 2y = 8§',
                'Subtract §4x§: §-2y = -4x + 8§',
                'Divide by -2: §y = 2x - 4§',
                'Slope = 2, y-intercept = -4',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'quadratic-equations',
      title: 'Quadratic Equations',
      content: {
        introduction:
          'Quadratic equations have the form ax² + bx + c = 0 where a ≠ 0. They can have zero, one, or two real solutions.',

        concepts: [
          {
            title: 'Factoring Method',
            prerequisiteNote:
              'You should know that if a product equals zero, at least one factor must be zero.',
            vocabulary: ['quadratic', 'factor', 'zero product', 'solution'],
            explanation:
              'When a quadratic can be factored, set each factor equal to zero to find solutions.',
            microExample: {
              problem: 'Solve §x(x-4)=0§',
              solution: [
                'A product is zero when one factor is zero.',
                '§x=0§ or §x-4=0§',
                'Solutions: §x=0§ or §x=4§',
              ],
            },
            greTrap:
              'Do not divide by a variable expression unless you know it is not zero; you may lose a solution.',
            checkQuestion:
              'If §(x-2)(x+5)=0§, what are the two possible values of x?',
            example: {
              problem: 'Solve: §x^2 - 5x + 6 = 0§',
              solution: [
                'Factor: §(x - 2)(x - 3) = 0§',
                'Set each factor to zero:',
                '§x - 2 = 0§ → §x = 2§',
                '§x - 3 = 0§ → §x = 3§',
                'Solutions: §x = 2§ or §x = 3§',
              ],
            },
          },
          {
            title: 'Quadratic Formula',
            prerequisiteNote:
              'Use this when factoring is not obvious or when the problem asks about number of solutions.',
            vocabulary: ['a', 'b', 'c', 'discriminant', 'real solutions'],
            explanation:
              'For §ax^2 + bx + c = 0§, use §x = (-b ± sqrt(b^2 - 4ac)) / (2a)§',
            microExample: {
              problem: 'For §x^2 - 4x + 4=0§, what is the discriminant?',
              solution: [
                'Here §a=1§, §b=-4§, §c=4§',
                '§b^2-4ac = 16 - 16 = 0§',
                'A discriminant of 0 means exactly one real solution.',
              ],
            },
            greTrap:
              'The discriminant alone can answer many GRE questions without solving for x.',
            checkQuestion:
              'If the discriminant is negative, how many real solutions are there?',
            example: {
              problem: 'Solve: §2x^2 + 3x - 2 = 0§',
              solution: [
                'Identify: §a = 2§, §b = 3§, §c = -2§',
                'Calculate discriminant: §b^2 - 4ac = 9 - 4(2)(-2) = 9 + 16 = 25§',
                'Apply formula: §x = (-3 ± sqrt(25)) / (2·2) = (-3 ± 5) / 4§',
                'Solutions: §x = (-3 + 5)/4 = 1/2§ or §x = (-3 - 5)/4 = -2§',
              ],
            },
          },
          {
            title: 'Completing the Square',
            explanation:
              'Transform §ax^2 + bx + c§ into §a(x - h)^2 + k§ form to find vertex and solutions.',
            example: {
              problem: 'Complete the square: §x^2 + 6x + 5 = 0§',
              solution: [
                'Move constant: §x^2 + 6x = -5§',
                'Add §(6/2)^2 = 9§ to both sides: §x^2 + 6x + 9 = -5 + 9 = 4§',
                'Factor: §(x + 3)^2 = 4§',
                'Take square root: §x + 3 = ±2§',
                'Solutions: §x = -3 + 2 = -1§ or §x = -3 - 2 = -5§',
              ],
            },
          },
          {
            title: 'Higher-Degree Polynomial Strategy',
            prerequisiteNote:
              'GRE higher-degree equations are usually designed to factor or simplify; they rarely require advanced algebra.',
            vocabulary: ['polynomial', 'common factor', 'substitution', 'difference of squares'],
            explanation:
              'For equations with powers above 2, do not expand first. Look for a common factor, a difference of squares, repeated structure, or substitution such as letting §u=x^2§.',
            microExample: {
              problem: 'Solve §x^3 - 4x = 0§',
              solution: [
                'Factor out the common §x§: §x(x^2-4)=0§',
                'Use difference of squares: §x(x-2)(x+2)=0§',
                'Solutions: §x=-2, 0, 2§',
              ],
            },
            greTrap:
              'Expanding usually makes GRE polynomial questions harder. Factor first.',
            checkQuestion:
              'What common factor can you remove from §2x^3 + 6x^2§?',
            example: {
              problem: 'Solve: §x^4 - 5x^2 + 4 = 0§',
              solution: [
                'Notice repeated §x^2§ structure. Let §u=x^2§.',
                'Then §u^2 - 5u + 4 = 0§',
                'Factor: §(u-1)(u-4)=0§, so §u=1§ or §u=4§',
                'Return to x: §x^2=1§ or §x^2=4§',
                'Solutions: §x=-2, -1, 1, 2§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'inequalities',
      title: 'Inequalities',
      content: {
        introduction:
          'Inequalities show relationships where one expression is greater than, less than, or equal to another. Solutions are typically ranges of values.',

        concepts: [
          {
            title: 'Linear Inequalities',
            explanation:
              'Solve linear inequalities like equations, but flip the inequality sign when multiplying or dividing by negative numbers.',
            example: {
              problem: 'Solve: §3x - 7 < 8§',
              solution: [
                'Add 7 to both sides: §3x < 15§',
                'Divide by 3: §x < 5§',
                'Solution: All real numbers less than 5',
                'In interval notation: §(-∞, 5)§',
              ],
            },
          },
          {
            title: 'Compound Inequalities',
            explanation:
              "Compound inequalities involve 'and' or 'or' conditions.",
            example: {
              problem: 'Solve: §-2 < 3x + 1 < 10§',
              solution: [
                'Subtract 1 from all parts: §-3 < 3x < 9§',
                'Divide by 3: §-1 < x < 3§',
                'Solution: All real numbers between -1 and 3',
                'In interval notation: §(-1, 3)§',
              ],
            },
          },
          {
            title: 'Quadratic Inequalities',
            explanation:
              'For quadratic inequalities, find zeros and test intervals to determine solution regions.',
            example: {
              problem: 'Solve: §x^2 - 4x - 5 > 0§',
              solution: [
                'Factor: §(x - 5)(x + 1) > 0§',
                'Find zeros: §x = 5§ and §x = -1§',
                'Test intervals: §(-∞, -1), (-1, 5), (5, ∞)§',
                'Test §x = -2§: §(-2-5)(-2+1) = (-7)(-1) = 7 > 0§ ✓',
                'Solution: §x < -1§ or §x > 5§',
              ],
            },
          },
        ],
      },
    },
    {
      id: 'systems',
      title: 'Systems of Equations',
      content: {
        introduction:
          'Systems of equations involve multiple equations with multiple variables. Common methods include substitution, elimination, and graphing.',

        concepts: [
          {
            title: 'Substitution Method',
            explanation:
              'Solve one equation for a variable, then substitute into the other equation.',
            example: {
              problem: 'Solve: §x + y = 7§ and §2x - y = 5§',
              solution: [
                'From first equation: §y = 7 - x§',
                'Substitute into second: §2x - (7 - x) = 5§',
                'Simplify: §2x - 7 + x = 5§ → §3x = 12§ → §x = 4§',
                'Find y: §y = 7 - 4 = 3§',
                'Solution: (4, 3)',
              ],
            },
          },
          {
            title: 'Elimination Method',
            explanation: 'Add or subtract equations to eliminate one variable.',
            example: {
              problem: 'Solve: §3x + 2y = 16§ and §5x - 2y = 8§',
              solution: [
                'Add the equations to eliminate y:',
                '§(3x + 2y) + (5x - 2y) = 16 + 8§',
                'Simplify: §8x = 24§ → §x = 3§',
                'Substitute back: §3(3) + 2y = 16§ → §9 + 2y = 16§ → §y = 3.5§',
                'Solution: (3, 3.5)',
              ],
            },
          },
          {
            title: 'Systems with No Solution or Infinite Solutions',
            explanation:
              'Some systems have no solution (inconsistent) or infinitely many solutions (dependent).',
            example: {
              problem: 'Analyze: §2x + 3y = 6§ and §4x + 6y = 12§',
              solution: [
                'Multiply first equation by 2: §4x + 6y = 12§',
                'This is identical to the second equation',
                'The system has infinitely many solutions',
                'Any point on the line §2x + 3y = 6§ is a solution',
              ],
            },
          },
        ],
      },
    },
  ];

  const practiceQuestions = [
    {
      type: 'timed',
      timeLimit: 90,
      question: 'Solve for x: §4x - 12 = 2x + 8§',
      options: ['§x = 10§', '§x = 8§', '§x = 12§', '§x = 6§', '§x = 4§'],
      correct: 0,
      explanation:
        '§4x - 12 = 2x + 8§. Subtract §2x§: §2x - 12 = 8§. Add 12: §2x = 20§. Divide by 2: §x = 10§.',
    },
    {
      type: 'timed',
      timeLimit: 120,
      question: 'What are the solutions to §x^2 - 7x + 12 = 0§?',
      options: [
        '§x = 3, 4§',
        '§x = 2, 6§',
        '§x = 1, 12§',
        '§x = 3, -4§',
        '§x = -3, -4§',
      ],
      correct: 0,
      explanation:
        'Factor: §x^2 - 7x + 12 = (x - 3)(x - 4) = 0§. Therefore §x = 3§ or §x = 4§.',
    },
    {
      type: 'timed',
      timeLimit: 100,
      question: 'Solve the inequality: §3x + 5 > 14§',
      options: ['§x > 3§', '§x > 9§', '§x < 3§', '§x > 6§', '§x < 9§'],
      correct: 0,
      explanation: '§3x + 5 > 14§. Subtract 5: §3x > 9§. Divide by 3: §x > 3§.',
    },
    {
      type: 'timed',
      timeLimit: 150,
      question:
        'In the system §x + 2y = 8§ and §3x - y = 3§, what is the value of x?',
      options: ['§x = 2§', '§x = 3§', '§x = 1§', '§x = 4§', '§x = 0§'],
      correct: 0,
      explanation:
        'To solve the system, we can use the substitution method. From the second equation, we can express y in terms of x: §y = 3x - 3§. Now substitute this into the first equation: §x + 2(3x - 3) = 8§. This simplifies to §x + 6x - 6 = 8§, which gives §7x = 14§. Solving for x, we get §x = 2§.',
    },
    {
      type: 'timed',
      timeLimit: 110,
      question: 'Which of the following is equivalent to §(x + 3)^2§?',
      options: [
        '§x^2 + 6x + 9§',
        '§x^2 + 9§',
        '§x^2 + 3x + 9§',
        '§x^2 + 6x + 6§',
        '§x^2 + 3x + 6§',
      ],
      correct: 0,
      explanation:
        '§(x + 3)^2 = (x + 3)(x + 3) = x^2 + 3x + 3x + 9 = x^2 + 6x + 9§.',
    },
    {
      type: 'timed',
      timeLimit: 140,
      question: 'If §x^2 - 3x - 10 = 0§, what are the possible values of §x§?',
      options: [
        '§x = 5§ or §x = -2§',
        '§x = -5§ or §x = 2§',
        '§x = 10§ or §x = -1§',
        '§x = 3§ or §x = -10§',
        '§x = 5§ or §x = 2§',
      ],
      correct: 0,
      explanation:
        'Factor: §(x - 5)(x + 2) = 0§. Therefore §x = 5§ or §x = -2§.',
    },
    {
      type: 'timed',
      timeLimit: 135,
      question: 'Solve the inequality: §-2x + 6 ≥ 10§',
      options: ['§x ≤ -2§', '§x ≥ -2§', '§x ≤ 2§', '§x ≥ 2§', '§x ≤ 8§'],
      correct: 0,
      explanation:
        '§-2x + 6 ≥ 10§. Subtract 6: §-2x ≥ 4§. Divide by -2 (flip sign): §x ≤ -2§.',
    },
    {
      type: 'untimed',
      question:
        'A linear equation has a slope of -2 and passes through the point (1, 5). What is the equation in slope-intercept form?',
      options: [
        '§y = -2x + 7§',
        '§y = -2x + 3§',
        '§y = -2x + 5§',
        '§y = 2x + 3§',
        '§y = -2x - 7§',
      ],
      correct: 0,
      explanation:
        'Using point-slope form: §y - 5 = -2(x - 1)§ → §y - 5 = -2x + 2§ → §y = -2x + 7§.',
    },
    {
      type: 'untimed',
      question:
        'What is the discriminant of the quadratic equation §3x^2 - 4x + 1 = 0§?',
      options: ['§4§', '§8§', '§16§', '§12§', '§1§'],
      correct: 0,
      explanation:
        'The discriminant is §b^2 - 4ac = (-4)^2 - 4(3)(1) = 16 - 12 = 4§.',
    },
    {
      type: 'untimed',
      question: 'Solve the compound inequality: §-3 ≤ 2x - 1 < 5§',
      options: [
        '§-1 ≤ x < 3§',
        '§0 ≤ x < 4§',
        '§-2 ≤ x < 6§',
        '§-1 < x ≤ 3§',
        '§0 < x ≤ 4§',
      ],
      correct: 0,
      explanation:
        'Add 1 to all parts: §-2 ≤ 2x < 6§. Divide by 2: §-1 ≤ x < 3§.',
    },
    {
      type: 'untimed',
      question:
        'In a system of equations, if one equation is §2x + 3y = 12§ and the solution is (3, 2), what could be the second equation?',
      options: [
        '§x - y = 1§',
        '§x + y = 6§',
        '§2x - y = 4§',
        '§3x + y = 11§',
        'Only A, C, and D',
      ],
      correct: 4,
      explanation:
        'Verify (3, 2) in each equation: A) §x - y = 1§: §3 - 2 = 1§ ✓. B) §x + y = 6§: §3 + 2 = 5 ≠ 6§ ✗. C) §2x - y = 4§: §6 - 2 = 4§ ✓. D) §3x + y = 11§: §9 + 2 = 11§ ✓. Therefore A, C, and D all work, but not B.',
    },
    {
      type: 'untimed',
      question:
        'If §x^2 - 6x + k = 0§ has exactly one solution, what is the value of k?',
      options: ['§9§', '§6§', '§3§', '§0§', '§-9§'],
      correct: 0,
      explanation:
        'For exactly one solution, the discriminant must equal zero: §b^2 - 4ac = 0§. Here: §(-6)^2 - 4(1)(k) = 0§ → §36 - 4k = 0§ → §k = 9§.',
    },
    {
      type: 'untimed',
      question: 'Solve: §2^x = 64§',
      options: ['§x = 5§', '§x = 6§', '§x = 7§', '§x = 8§', '§x = 9§'],
      correct: 1,
      explanation:
        '§2^x = 64§. Since §64 = 2^6§, we have §x = 6§.',
    },
    {
      type: 'untimed',
      question:
        'In a system of equations, if §x + y = 10§ and §x - y = 4§, what is §x§?',
      options: ['§7§', '§6§', '§8§', '§5§', '§9§'],
      correct: 0,
      explanation:
        'Add the equations: §(x + y) + (x - y) = 10 + 4§ → §2x = 14§ → §x = 7§.',
    },
    {
      type: 'untimed',
      question: 'If §2x - 3 > 7§, what is the smallest integer value of §x§?',
      options: ['§6§', '§5§', '§7§', '§4§', '§8§'],
      correct: 0,
      explanation:
        '§2x - 3 > 7§ → §2x > 10§ → §x > 5§. Smallest integer greater than 5 is §6§.',
    },
    {
      type: 'timed',
      question:
        'In a system of equations, if §x + y = 10§ and §x - y = 4§, what is §x§?',
      options: ['§7§', '§6§', '§8§', '§5§', '§9§'],
      correct: 0,
      explanation:
        'Add the equations: §(x + y) + (x - y) = 10 + 4§ → §2x = 14§ → §x = 7§.',
    },
    {
      type: 'timed',
      question: 'If §2x - 3 > 7§, what is the smallest integer value of §x§?',
      options: ['§6§', '§5§', '§7§', '§4§', '§8§'],
      correct: 0,
      explanation:
        '§2x - 3 > 7§ → §2x > 10§ → §x > 5§. Smallest integer greater than 5 is §6§.',
    },
    {
      type: 'untimed',
      questionType: 'quantitative_comparison',
      question: 'If §2x + 3 = 15§, compare the two quantities.',
      quantityA: '§x§',
      quantityB: '§5§',
      correct: 0,
      explanation:
        'Compare the two quantities directly. Quantity A is greater.',
    },
    {
      type: 'untimed',
      questionType: 'multiple_choice_multi',
      question: 'Which equations have solution §x = 3§? Select all that apply.',
      options: [
        '§2x + 1 = 7§',
        '§x^2 = 9§',
        '§x + 4 = 8§',
        '§3x - 2 = 7§',
        '§x/3 = 2§',
      ],
      correct: [0, 1, 3],
      explanation:
        'Substitute §x = 3§. Choices A, B, and D are true; C gives 7 and E gives 1.',
    },
    {
      type: 'timed',
      timeLimit: 105,
      questionType: 'numeric_entry',
      question: 'If §3x - 7 = 20§, enter the value of §x§.',
      correctAnswer: '9',
      acceptedAnswers: ['9'],
      explanation: 'Add 7 to get §3x = 27§, so §x = 9§.',
    },
  ];

  const quickReference = [
    'Linear equations: isolate the variable by undoing operations in reverse order',
    'Quadratic formula: §x = (-b ± sqrt(b^2-4ac))/2a§',
    'Perfect square: §(a±b)^2 = a^2 ± 2ab + b^2§',
    'Difference of squares: §a^2 - b^2 = (a+b)(a-b)§',
    'Higher-degree equations: factor first, substitute if powers repeat',
    'Absolute value equation: §|A| = c§ splits into §A = c§ or §A = -c§',
    'Slope formula: §m = (y_2-y_1)/(x_2-x_1)§',
  ];

  const getStartedWithProps = {
    title: 'Need help with Algebra concepts?',
    spaceName: 'Quant hacks',
    humanCoachTitle: 'Talk to a GRE coach',
    liwazaAITitle: 'Ask AI',
    communityTitle: 'Ask other students',
    showHumanCoach: true,
    showLiwazaAI: true,
    showCommunity: true,
    aifirstmessage:
      'I need help understanding Algebra concepts for the GRE Quantitative section',
    aicontext:
      'Master linear equations, quadratics, inequalities, and systems of equations',
  };

  return (
    <LessonTemplate
      lessonId="algebra"
      lessonTitle="Algebra"
      lessonIcon={<FiActivity />}
      lessonDescription="Master linear equations, quadratics, inequalities, and systems of equations"
      sections={sections}
      practiceQuestions={practiceQuestions}
      quickReference={quickReference}
      getStartedWithProps={getStartedWithProps}
      supplementalPracticeSlot={<QuantLessonPracticeSlot lessonId="algebra" />}
    />
  );
};

export default AlgebraLesson;
